import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Upload,
  Plus,
  Eye,
  ArrowLeft,
  ArrowRight,
  Undo2,
  Trash2,
  X,
  Check,
  Settings2,
  Search,
} from "lucide-react";
import { api } from "@/lib/api";
import { WorkflowPage, WORKFLOW_TYPES } from "./DocumentWorkflowBuilder";
import {
  emptyWorkflow,
  FIELD_USES,
  fieldUse,
  newField,
  normalizeWorkflow,
  patchCanvasField,
  fieldUsePatch,
  removeCanvasField,
  mergeDetectedFields,
} from "./documentCanvasModel";
import "./document-canvas.css";
const MAX_PDF_BYTES = 100 * 1024 * 1024;
const message = (e) =>
  e.response?.status === 413
    ? "This PDF is too large. Choose a PDF up to 100 MB."
    : typeof e.response?.data?.detail === "string"
    ? e.response.data.detail
    : e.message || "The document could not be saved. Please try again.";
const ROLES = [
  ["massage_therapist", "Massage Therapist"],
  ["front_desk", "Experience Guide"],
  ["boutique_manager", "General Manager"],
];
export default function DocumentCanvasBuilder({
  existing,
  onCreated,
  onSaved,
  onCancel,
  onWord,
  onBack,
}) {
  const [wf, setWf] = useState(existing?.workflow || emptyWorkflow),
    [title, setTitle] = useState(existing?.title || ""),
    [category, setCategory] = useState(existing?.category || ""),
    [doc, setDoc] = useState(existing || null),
    [selectedId, setSelected] = useState(null),
    [page, setPage] = useState(0),
    [trying, setTrying] = useState(false),
    [owner, setOwner] = useState("employee"),
    [values, setValues] = useState({}),
    [history, setHistory] = useState([]),
    [placing, setPlacing] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [modal, setModal] = useState(null),
    [blank, setBlank] = useState(false),
    [dirty, setDirty] = useState(false),
    [preview, setPreview] = useState(null),
    [previewKey, setPreviewKey] = useState(""),
    [previewError, setPreviewError] = useState(""),
    [attempt, setAttempt] = useState(0),
    [confirmed, setConfirmed] = useState(false),
    [height, setHeight] = useState(400);
  const editSerial = useRef(0);
  const file = useRef(null),
    dialog = useRef(null),
    workspace = useRef(null);
  const current = wf.fields.find((f) => f.id === selectedId),
    ready = !!wf.source_id && !!title.trim();
  const key = ready
    ? JSON.stringify([wf, title.trim(), category, page, doc?.id])
    : "";
  const old = previewKey ? JSON.parse(previewKey) : null;
  const samePage = old && old[0].source_id === wf.source_id && old[3] === page;
  const previewReady = !!preview && previewKey === key && !previewError;
  useLayoutEffect(() => {
    const el = workspace.current;
    if (!el) return;
    const fit = () => setHeight(Math.max(150, el.clientHeight));
    fit();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    window.addEventListener("resize", fit);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", fit);
    };
  }, []);
  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (modal) {
      if (d.showModal) d.showModal();
      else d.setAttribute("open", "");
    } else if (d.close) d.close();
    else d.removeAttribute("open");
  }, [modal]);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    setPreviewError("");
    const timer = setTimeout(async () => {
      const [workflow, t, c, p, id] = JSON.parse(key);
      try {
        const r = await api.post("/document-workflows/preview", {
          workflow,
          title: t,
          category: c,
          page: p,
          document_id: id,
        });
        if (!cancelled) {
          setPreview(r.data);
          setPreviewKey(key);
        }
      } catch (e) {
        if (!cancelled) setPreviewError(message(e));
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [key, attempt]);
  useEffect(() => {
    const before = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [dirty]);
  const change = (next) => {
    editSerial.current += 1;
    setHistory((h) => [...h.slice(-39), wf]);
    setWf(next);
    setDirty(true);
    setConfirmed(false);
  };
  const patch = (id, p) => change(patchCanvasField(wf, id, p));
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      return await fn();
    } catch (e) {
      setError(message(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const saveRaw = async () => {
    const serial = editSerial.current;
    if (!ready) throw Error("Upload a document and give it a title first.");
    const payload = {
      title: title.trim(),
      category,
      workflow: wf,
      expected_version: doc?.version,
    };
    const r = doc
      ? await api.put(`/document-workflows/${doc.id}`, payload)
      : await api.post("/document-workflows", payload);
    setDoc(r.data);
    if (serial !== editSerial.current)
      throw Error(
        "An earlier draft was saved. Save your latest changes before publishing.",
      );
    setDirty(false);
    return r.data;
  };
  const upload = (f) =>
    run(async () => {
      if (!f) return;
      if (f.size > MAX_PDF_BYTES)
        throw Error(`This PDF is ${(f.size / 1024 / 1024).toFixed(1)} MB. Choose a PDF up to 100 MB.`);
      if (!blank)
        throw Error(
          "Confirm this is a blank reusable template before uploading.",
        );
      const data = new FormData();
      data.append("file", f);
      data.append("blank_template", "true");
      const r = await api.post("/document-workflows/sources", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (r.data.format !== "pdf")
        throw Error("Choose a PDF to keep its original pages.");
      change(
        normalizeWorkflow({
          ...emptyWorkflow(),
          source_id: r.data.source_id,
          fields: r.data.fields,
          blocks: r.data.blocks || [],
        }),
      );
      if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
      setPage(0);
      setSelected(null);
      setValues({});
      setPreview(null);
      setPreviewKey("");
      setNotice(r.data.field_detection === 'printed_lines' ? `${r.data.fields.length} fields found.${r.data.fields.some(f=>f.kind==='signature') ? ' Signatures and dates configured.' : ''}` : '');
      setModal(null);
    });
  const detectFields = () => run(async () => {
    const serial = editSerial.current;
    setNotice('Finding fields…');
    try {
      const {data} = await api.post(`/document-workflows/sources/${wf.source_id}/detect-fields`,{document_id:doc?.id});
      if(serial !== editSerial.current) throw Error('The document changed. Choose Find fields again.');
      const fields = mergeDetectedFields(wf.fields,data.fields);
      const added = fields.length-wf.fields.length;
      const configured = fields.slice(0,wf.fields.length).filter((f,i)=>f !== wf.fields[i]).length;
      if(added || configured) change(normalizeWorkflow({...wf,fields}));
      setPlacing(false);
      setNotice(configured ? 'Signatures and dates configured.' : added ? `${added} ${added===1?'field':'fields'} found. Review field types.` : 'No additional fields found. Use Add field for any remaining areas.');
    } catch(e) {setNotice('');throw e;}
  });
  const finish = () =>
    run(async () => {
      if (!previewReady)
        throw Error(
          "Wait for the current document to load, or fix the field settings shown below.",
        );
      if (dirty || !doc) await saveRaw();
      setConfirmed(false);
      setModal("review");
    });
  const publish = () =>
    run(async () => {
      if (!confirmed || dirty || !doc || !previewReady)
        throw Error("Review and confirm the saved version before publishing.");
      const data = {
        expected_version: doc.version,
        expected_hash: doc.workflow_hash,
        confirmed: true,
      };
      await api.post(`/document-workflows/${doc.id}/review`, data);
      await api.post(`/document-workflows/${doc.id}/publish`, data);
      setDirty(false);
      setModal(null);
      onCreated?.(doc.id);
      onSaved?.();
    });
  const localDate = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
  }).format(new Date());
  const testValues = { ...values };
  for (const f of wf.fields) {
    if (f.prefill === "company" || f.prefill === "text")
      testValues[f.id] = f.prefill_text;
    if (f.prefill === "completion_date") testValues[f.id] = localDate;
  }
  const simulated = (signer) => {
    const sig = wf.fields.find(
      (f) => f.kind === "signature" && f.signer === signer && values[f.id],
    );
    return sig
      ? {
          signature: {
            typed_name: values[sig.id],
            at: new Date().toISOString(),
          },
        }
      : null;
  };
  const counts = { employee: 0, general_manager: 0, automatic: 0 };
  for (const f of wf.fields)
    counts[
      (f.prefill && f.prefill !== "none") || f.kind === "signing_date"
        ? "automatic"
        : f.signer
    ]++;
  const detailChange = (p) => change(normalizeWorkflow({ ...wf, ...p }));
  return (
    <div className="document-workflow wf-builder wf-canvas">
      <h1 className="wf-canvas-title">{title || "Documents"}</h1>
      <div className="wf-canvas-toolbar">
        <div className="wf-canvas-tools">
          {onBack && <button disabled={busy} onClick={() => {
            if (!dirty || window.confirm("Leave this document? Unsaved changes will be lost.")) onBack();
          }}><ArrowLeft size={15}/>Documents</button>}
          {trying ? (
            <>
              <button onClick={() => setTrying(false)}>
                <ArrowLeft size={15} />
                Edit fields
              </button>
              <label className="wf-canvas-try">
                Try as{" "}
                <select
                  aria-label="Try as"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                >
                  <option value="employee">Employee</option>
                  <option value="general_manager">General Manager</option>
                </select>
              </label>
            </>
          ) : (
            <>
              <button disabled={busy} onClick={() => setModal("upload")}>
                <Upload size={15} />
                {wf.source_id ? "Replace document" : "Upload document"}
              </button>
              <button
                disabled={!wf.source_id || busy}
                aria-pressed={placing}
                onClick={() => {
                  setPlacing(!placing);
                  setSelected(null);
                }}
              >
                <Plus size={16} />
                Add field
              </button>
              {wf.source_id && <button disabled={busy} onClick={detectFields}><Search size={15}/>Find fields</button>}
              <button
                aria-label="Undo field change"
                title="Undo"
                disabled={!history.length || busy}
                onClick={() => {
                  setWf(history[history.length - 1]);
                  setHistory((h) => h.slice(0, -1));
                  setDirty(true);
                  setSelected(null);
                }}
              >
                <Undo2 size={16} />
              </button>
            </>
          )}
        </div>
        <div className="wf-canvas-tools">
          <button
            aria-label="Document details"
            title="Document details"
            disabled={busy}
            onClick={() => setModal("details")}
          >
            <Settings2 size={16} />
          </button>
          <button
            disabled={!ready || busy || !dirty}
            onClick={() => run(saveRaw)}
          >
            {busy ? "Saving…" : "Save draft"}
          </button>
          {!trying && (
            <button
              disabled={!previewReady || busy}
              onClick={() => {
                setTrying(true);
                setValues({});
                setPlacing(false);
                setSelected(null);
              }}
            >
              <Eye size={15} />
              Try form
            </button>
          )}
          <button
            className="wf-primary"
            disabled={!previewReady || busy}
            onClick={finish}
          >
            Finish setup
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
      {notice && <p role="status" className="wf-canvas-notice">{notice}</p>}
      {error && !modal && (
        <p role="alert" className="wf-error">
          {error}
        </p>
      )}
      {previewError && (
        <p role="alert" className="wf-error">
          {previewError}{" "}
          <button onClick={() => setAttempt((a) => a + 1)}>Try again</button>
        </p>
      )}
      <div
        ref={workspace}
        className={"wf-canvas-workspace" + (trying ? " is-trying" : "")}
        style={{ "--wf-canvas-height": height + "px" }}
      >
        <WorkflowPage
          preview={previewReady || samePage ? preview : null}
          page={page}
          setPage={(p) => {
            setPage(p);
            setSelected(null);
          }}
          fields={wf.fields}
          editing={!trying}
          selected={current}
          onSelect={(id) => {
            setSelected(id);
            setPlacing(false);
          }}
          onChange={patch}
          fitContainer
          placing={placing}
          onPlace={(x, y) => {
            const f = newField(page, x, y);
            change(normalizeWorkflow({ ...wf, fields: [...wf.fields, f] }));
            setSelected(f.id);
            setPlacing(false);
          }}
          values={testValues}
          onValue={(id, v) => setValues((s) => ({ ...s, [id]: v }))}
          simulation={trying}
          activeSigner={owner}
          completion={simulated("employee")}
          counter={simulated("general_manager")}
          canFill
          canCounterFill={trying}
          emptyMessage={
            wf.source_id
              ? "Opening document…"
              : "Upload a blank PDF to begin. Existing fillable fields will appear automatically."
          }
        />
        {!trying && (
          <aside className="wf-canvas-inspector" aria-label="Field settings">
            {current ? (
              <>
                <div className="wf-canvas-inspector-title">
                  <h2>{current.label}</h2>
                  <button
                    aria-label="Close field settings"
                    onClick={() => setSelected(null)}
                  >
                    <X size={17} />
                  </button>
                </div>
                <div className="wf-canvas-fields">
                  {current.read_only ? (
                    <p>This original field is locked.</p>
                  ) : (
                    <>
                      <label>
                        What belongs here?
                        <select
                          aria-label="What belongs here?"
                          value={fieldUse(current)}
                          disabled={current.kind === "choice"}
                          onChange={(e) =>
                            patch(
                              current.id,
                              fieldUsePatch(current, e.target.value),
                            )
                          }
                        >
                          {current.kind === "choice" ? (
                            <option value="choice">
                              Original dropdown choices
                            </option>
                          ) : (
                            FIELD_USES.map(([v, l]) => (
                              <option key={v} value={v}>
                                {l}
                              </option>
                            ))
                          )}
                        </select>
                      </label>
                      {["company", "text"].includes(current.prefill) && (
                        <label>
                          {current.prefill === "company"
                            ? "Company name"
                            : "Text to insert"}
                          <textarea
                            aria-label={
                              current.prefill === "company"
                                ? "Company name"
                                : "Text to insert"
                            }
                            rows={3}
                            maxLength={2000}
                            value={current.prefill_text || ""}
                            onChange={(e) =>
                              patch(current.id, {
                                prefill_text: e.target.value,
                              })
                            }
                          />
                        </label>
                      )}
                      {current.kind === "signing_date" && (
                        <label>
                          Whose signing date?
                          <select
                            value={current.signer}
                            onChange={(e) =>
                              patch(current.id, { signer: e.target.value })
                            }
                          >
                            <option value="employee">Employee</option>
                            <option value="general_manager">
                              General Manager
                            </option>
                          </select>
                        </label>
                      )}
                      {(current.prefill && current.prefill !== "none") ||
                      current.kind === "signing_date" ? (
                        <p className="wf-canvas-auto">
                          <Check size={15} />
                          {current.kind === "signing_date"
                            ? "Added when this person signs."
                            : current.prefill === "completion_date"
                              ? "Added when the form is completed."
                              : "Filled automatically."}
                        </p>
                      ) : (
                        <>
                          <label>
                            Who fills this in?
                            <select
                              aria-label="Who fills this in?"
                              value={current.signer}
                              onChange={(e) =>
                                patch(current.id, { signer: e.target.value })
                              }
                            >
                              <option value="employee">Employee</option>
                              <option value="general_manager">
                                General Manager
                              </option>
                            </select>
                          </label>
                          <label className="wf-canvas-check">
                            <input
                              type="checkbox"
                              checked={current.required}
                              onChange={(e) =>
                                patch(current.id, {
                                  required: e.target.checked,
                                })
                              }
                            />
                            Required
                          </label>
                        </>
                      )}
                      <details>
                        <summary>More options</summary>
                        <label>
                          Field name
                          <input
                            aria-label="Field name"
                            value={current.label}
                            maxLength={100}
                            onChange={(e) =>
                              patch(current.id, { label: e.target.value })
                            }
                          />
                        </label>
                        {!current.existing_name && (
                          <button
                            className="wf-canvas-danger"
                            onClick={() => {
                              change(removeCanvasField(wf, current.id));
                              setSelected(null);
                            }}
                          >
                            <Trash2 size={15} />
                            Remove field
                          </button>
                        )}
                      </details>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="wf-canvas-empty">
                <h2>{placing ? "Place the field" : wf.source_id && !wf.fields.length ? "Add fields" : "Select a field"}</h2>
                <p>
                  {placing
                    ? "Click the document where the new field belongs."
                    : wf.source_id && !wf.fields.length
                      ? "Choose Find fields to detect printed lines, or Add field to place one yourself."
                      : "Click a box on the document to choose what goes there."}
                </p>
                {wf.source_id && wf.fields.length > 0 && <span>{wf.fields.length} fields found</span>}
              </div>
            )}
          </aside>
        )}
      </div>
      {trying && <span className="wf-canvas-stamp">TRY FORM · NOT SIGNED</span>}
      <dialog
        ref={dialog}
        className="wf-canvas-dialog"
        onCancel={(e) => {
          if (busy) e.preventDefault();
          else setModal(null);
        }}
      >
        <button
          aria-label="Close dialog"
          className="wf-canvas-close"
          disabled={busy}
          onClick={() => setModal(null)}
        >
          <X size={18} />
        </button>
        {modal === "upload" && (
          <>
            <h2>{wf.source_id ? "Replace document" : "Upload document"}</h2>
            <input
              ref={file}
              type="file"
              accept=".pdf,application/pdf"
              hidden
              disabled={!blank || busy}
              onChange={(e) => {
                upload(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <label className="wf-canvas-check">
              <input
                type="checkbox"
                checked={blank}
                disabled={busy}
                onChange={(e) => setBlank(e.target.checked)}
              />
              Confirm this is a blank template with no completed personal information.
            </label>
            <p className="wf-upload-limit">PDF · Up to 100 MB · 500 pages</p>
            {wf.source_id && (
              <p>
                Replacing the source resets its field settings. Save a draft
                first to keep the current version.
              </p>
            )}
            <button
              className="wf-primary"
              disabled={!blank || busy}
              onClick={() => file.current.click()}
            >
              <Upload size={16} />
              {busy ? "Uploading…" : "Choose PDF"}
            </button>
            {busy && <p role="status">Uploading and preparing your document…</p>}
            {!wf.source_id && !existing && (
              <button
                disabled={busy}
                onClick={() => {
                  setModal(null);
                  onWord?.();
                }}
              >
                Create from Word document
              </button>
            )}
          </>
        )}
        {modal === "details" && (
          <>
            <h2>Document details</h2>
            <label>
              Document title
              <input
                value={title}
                maxLength={200}
                onChange={(e) => {
                  editSerial.current += 1;
                  setTitle(e.target.value);
                  setDirty(true);
                }}
              />
            </label>
            <label>
              Document type
              <select
                value={wf.document_type}
                onChange={(e) =>
                  detailChange({ document_type: e.target.value })
                }
              >
                {WORKFLOW_TYPES.map(([v, l]) => (
                  <option value={v} key={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Library category
              <input
                value={category}
                maxLength={100}
                onChange={(e) => {
                  editSerial.current += 1;
                  setCategory(e.target.value);
                  setDirty(true);
                }}
              />
            </label>
            {!wf.fields.length && (
              <label className="wf-canvas-check">
                <input
                  type="checkbox"
                  checked={wf.completion === "acknowledge"}
                  onChange={(e) =>
                    detailChange({
                      completion: e.target.checked
                        ? "acknowledge"
                        : "read_only",
                    })
                  }
                />
                Require confirmation that it has been read
              </label>
            )}
            <details>
              <summary>Add to onboarding packet</summary>
              <p>
                Optional. These roles receive the published version in their
                onboarding packet, with boutique General Manager countersigning.
              </p>
              {ROLES.map(([v, l]) => (
                <label className="wf-canvas-check" key={v}>
                  <input
                    type="checkbox"
                    checked={wf.onboarding_roles.includes(v)}
                    onChange={(e) =>
                      detailChange({
                        onboarding_roles: e.target.checked
                          ? [...wf.onboarding_roles, v]
                          : wf.onboarding_roles.filter((r) => r !== v),
                      })
                    }
                  />
                  {l}
                </label>
              ))}
            </details>
            <button className="wf-primary" onClick={() => setModal(null)}>
              Done
            </button>
            {onCancel && (
              <button
                disabled={busy}
                onClick={() => {
                  if (!dirty || window.confirm("Discard unsaved changes?"))
                    onCancel();
                }}
              >
                Close editor
              </button>
            )}
          </>
        )}
        {modal === "review" && (
          <>
            <h2>Ready to publish</h2>
            <p>{title}</p>
            <dl>
              <div>
                <dt>Employee fields</dt>
                <dd>{counts.employee}</dd>
              </div>
              <div>
                <dt>General Manager fields</dt>
                <dd>{counts.general_manager}</dd>
              </div>
              <div>
                <dt>Automatic fields</dt>
                <dd>{counts.automatic}</dd>
              </div>
            </dl>
            <p>
              {wf.counter_sign
                ? "The employee completes the form first. The boutique’s General Manager then completes their fields and countersigns."
                : wf.completion === "read_only"
                  ? "This document is read only."
                  : "The employee completes this form in their own account."}
            </p>
            <p>
              {wf.onboarding_roles.length
                ? "Included in onboarding for: " +
                  ROLES.filter(([r]) => wf.onboarding_roles.includes(r))
                    .map(([, l]) => l)
                    .join(", ") +
                  "."
                : "Not assigned to an onboarding packet."}
            </p>
            <label className="wf-canvas-check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              I reviewed this exact version and approve publishing it to the
              Documents library.
            </label>
            <button
              className="wf-primary"
              disabled={!confirmed || busy || dirty || !previewReady}
              onClick={publish}
            >
              {busy ? "Publishing…" : "Publish document"}
              <ArrowRight size={16} />
            </button>
            <button disabled={busy} onClick={() => setModal(null)}>
              Keep editing
            </button>
          </>
        )}
        {modal && error && <p role="alert" className="wf-error">{error}</p>}
      </dialog>
    </div>
  );
}
